/**
 * 一键模板 —— SPEC.md §4A.5
 *
 * 只分数学（general）与物理（physics）两类。3D 场景归入数学类，
 * 由 ggbBridge 的 3D 自动检测按需升级 applet，无需单独分类。
 *
 * 关键设计：每条 prompt 都内嵌完整约束（参数、动画、配色），
 * 给 AI 留尽可能小的发挥空间，使一句话即可产出"理想动图"——
 *   ✓ 滑块完整  ✓ 自动启动动画  ✓ 轨迹默认开启  ✓ 视窗合理  ✓ 配色统一
 */

export interface Template {
  id: string;
  icon: string;
  title: string;
  /** 一句话副标题，供卡片简略展示 */
  subtitle: string;
  prompt: string;
  domain: "general" | "physics";
}

export const TEMPLATES: Template[] = [
  // —— 物理 · 运动学 / 动力学 ——
  {
    id: "projectile",
    icon: "🏐",
    title: "斜抛运动",
    subtitle: "抛物线轨迹 + 速度矢量",
    prompt:
      "绘制斜抛运动动图：初速度 v0 范围 1~50 m/s（默认 20），仰角 theta 范围 0~π/2（默认 π/4），时间 t 范围 0~5 s。质点 P 自动沿抛物线运动并留下蓝色轨迹；同时显示绿色速度矢量 v 实时跟随。坐标轴标 x/m, y/m，视窗 -2~50 × -2~25，t 自动播放（increasing）。",
    domain: "physics"
  },
  {
    id: "circular",
    icon: "🎯",
    title: "圆周运动",
    subtitle: "半径 + 向心加速度矢量",
    prompt:
      "绘制匀速圆周运动动图：半径 r 范围 0.5~3（默认 2 m），角速度 omega 范围 0.5~5（默认 1 rad/s），时间 t 范围 0~10 s。质点 P 在圆周上运动留蓝色轨迹；用红色矢量画半径 OP、橙色矢量画指向圆心的向心加速度 a_c（缩放 0.3）。视窗 -4~4 × -4~4，t 自动播放。",
    domain: "physics"
  },
  {
    id: "pendulum",
    icon: "🪀",
    title: "单摆",
    subtitle: "小角近似周期摆动",
    prompt:
      "绘制单摆动图：摆长 L 范围 0.1~2 m（默认 1），初角 theta0 范围 0~π/3 rad（默认 π/6），时间 t 范围 0~10 s。用小角近似 θ=θ0·cos(ωt), ω=√(g/L)。蓝色摆线连接 O=(0,0) 与摆球 M，M 染红，自动留下轨迹。视窗 -1.5~1.5 × -1.5~0.3，t 自动播放（oscillating）。",
    domain: "physics"
  },
  {
    id: "spring",
    icon: "🌀",
    title: "弹簧振子",
    subtitle: "简谐振动 + 振动图线",
    prompt:
      "绘制水平弹簧振子动图：劲度系数 k 范围 1~50 N/m（默认 10），质量 m 范围 0.1~5 kg（默认 1），时间 t 范围 0~10 s。x(t) = A·cos(ωt)，ω=√(k/m)，A=1。质点 P=(x(t),0) 染红，自动留下水平轨迹；下方画振动曲线 (t, x(t))。坐标轴 t/s × x/m，视窗 0~10 × -1.5~1.5，t 自动播放。",
    domain: "physics"
  },
  {
    id: "wave",
    icon: "🌊",
    title: "横波传播",
    subtitle: "行波 y=A·sin(kx−ωt)",
    prompt:
      "绘制行波 y = A·sin(k·x − ω·t) 动图：振幅 A 范围 0.1~2（默认 1 m），波数 kw 范围 0.5~5（默认 1.5 rad/m），角频率 omega 范围 0.5~5（默认 2 rad/s），时间 t 范围 0~10 s。注意：滑块命名为 kw（不是 k）避免与 GGB 滑块速度冲突。函数 f(x) = A*sin(kw*x - omega*t) 用蓝色绘制。坐标轴 x/m × y/m，视窗 0~10 × -2.5~2.5，t 自动播放（increasing）。",
    domain: "physics"
  },
  {
    id: "dipole",
    icon: "🧲",
    title: "偶极子电场",
    subtitle: "电场矢量网格",
    prompt:
      "绘制偶极子电场矢量网格：电荷间距/2 为 d 范围 0.5~4 m（默认 2），电荷量 qmag 范围 0.5~3（默认 1）。+q 红色染色置于 A=(d,0)，−q 蓝色置于 B=(-d,0)。Ex(x,y)=qmag*(x-d)/((x-d)^2+y^2+0.01)^1.5 - qmag*(x+d)/((x+d)^2+y^2+0.01)^1.5，Ey 同理。Emag(x,y)=sqrt(Ex^2+Ey^2+0.001)，step=0.4。arrows = Sequence(Sequence(Vector((i,j),(i+step*Ex(i,j)/Emag(i,j), j+step*Ey(i,j)/Emag(i,j))), i,-4,4,1), j,-3,3,1)。视窗 -5~5 × -4~4。",
    domain: "physics"
  },
  {
    id: "lens",
    icon: "🔍",
    title: "凸透镜成像",
    subtitle: "三条特殊光线",
    prompt:
      "绘制薄凸透镜成像光路图：焦距 f 范围 1~5（默认 3），物距 u 范围 -10~-1.5（默认 -6，置于左侧）。物点 P=(u, 1)。根据透镜成像公式 1/v - 1/u = 1/f 计算像距 v=(f*u)/(u+f)，像点 Q=(v, -v/u)。画 y=0 的光轴；透镜在 x=0 画竖直线段；三条特殊光线：平行入射→经焦点出射、过中心直射、过左焦点入射→平行出射。视窗 -12~12 × -3~3。",
    domain: "physics"
  },
  {
    id: "incline",
    icon: "📐",
    title: "斜面受力",
    subtitle: "重力 / 支持力 / 摩擦力",
    prompt:
      "绘制 30° 斜面静止物块受力分析图：质量 m 范围 0.1~10 kg（默认 2），倾角 theta 范围 0~π/3（默认 π/6）。O=(0,0)，R=(5cos(theta),5sin(theta))，蓝色斜面 Segment(O,R)。物块 block=(2cos(theta),2sin(theta)) 染深色。使用 forceDiagram op 在 block 处画三个力：重力 G=(0,-m*g/10) 红色、支持力 N=(-sin(theta)*m*g*cos(theta)/10, cos(theta)*m*g*cos(theta)/10) 橙色、摩擦力 f=(-cos(theta)*m*g*sin(theta)/10, -sin(theta)*m*g*sin(theta)/10) 紫色。视窗 -2~6 × -3~4。",
    domain: "physics"
  },

  // —— 数学 ——
  {
    id: "sin-family",
    icon: "〰️",
    title: "正弦函数族",
    subtitle: "y=A·sin(kx+φ)",
    prompt:
      "绘制函数族 y = A·sin(kx + phi)：振幅 A 范围 0.5~3（默认 1），频率 k 范围 0.5~5（默认 1），相位 phi 范围 -π~π（默认 0）。f(x) = A*sin(k*x + phi) 用蓝色绘制。视窗 -2π~2π × -3.5~3.5。",
    domain: "general"
  },
  {
    id: "unit-circle",
    icon: "⭕",
    title: "单位圆与三角函数",
    subtitle: "角 θ 对应正弦/余弦线",
    prompt:
      "绘制单位圆三角函数示意动图：半径 r=1，角度 theta 滑块范围 0~2π（默认 0，step 0.02，单位 rad）。画单位圆 c=Circle((0,0),1) 蓝色。动点 P=(cos(theta),sin(theta)) 染红；半径 Segment((0,0),P) 绿色；从 P 向 x 轴作垂足 H=(cos(theta),0)，正弦线段 Segment(P,H) 橙色、余弦线段 Segment((0,0),H) 紫色；圆弧 Arc 标注 theta。坐标轴标 x, y，视窗 -1.5~1.5 × -1.5~1.5，theta 自动播放（increasing）。",
    domain: "general"
  },
  {
    id: "rose",
    icon: "🌹",
    title: "玫瑰线",
    subtitle: "极坐标 r=a·cos(kθ)",
    prompt:
      "绘制极坐标玫瑰线：振幅 a 范围 1~3（默认 2），花瓣参数 k 范围 2~7（默认 4，整数滑块 step=1）。曲线 rose = Curve(a*cos(k*t)*cos(t), a*cos(k*t)*sin(t), t, 0, 2π) 用红色绘制。视窗 -4~4 × -4~4。",
    domain: "general"
  },
  {
    id: "cycloid",
    icon: "🔁",
    title: "摆线",
    subtitle: "滚轮圆周点轨迹",
    prompt:
      "绘制摆线动图：半径 r 范围 0.5~3（默认 1），时间 t 范围 0~4π s。圆沿 x 轴滚动：圆心 C=(r*t, r)，圆周点 P=(r*t - r*sin(t), r - r*cos(t)) 染红留轨迹；当前圆 Circle(C, r) 用浅蓝绘制，半径 Segment(C,P) 用绿色。坐标轴 x × y，视窗 0~14 × -0.5~3，t 自动播放。",
    domain: "general"
  },
  {
    id: "conic",
    icon: "🥚",
    title: "圆锥曲线",
    subtitle: "可调椭圆 + 焦点",
    prompt:
      "绘制可调圆锥曲线：长半轴 a 范围 0.5~4（默认 3），短半轴 b 范围 0.5~4（默认 2）。Ellipse 中心 (0,0) 顶点 (a,0) 顶点 (0,b)，蓝色绘制；同时显示两个焦点 F1, F2（红色 Point）与焦点距离 c=sqrt(|a^2-b^2|)。视窗 -5~5 × -4~4。",
    domain: "general"
  },
  {
    id: "taylor",
    icon: "📈",
    title: "泰勒展开",
    subtitle: "sin(x) 的多项式逼近",
    prompt:
      "绘制 sin(x) 的泰勒多项式逼近：阶数 n 范围 1~12（默认 5，整数滑块 step=1）。原函数 f(x)=sin(x) 蓝色；泰勒展开 T(x) = TaylorPolynomial(sin(x), 0, n) 红色（注意 TaylorPolynomial 命令）；视窗 -2π~2π × -2~2。",
    domain: "general"
  },
  {
    id: "cube-section",
    icon: "📦",
    title: "正方体截面",
    subtitle: "3D · 平面截立体",
    prompt:
      "画边长为 3 的正方体 ABCD-EFGH，展示平面 ACF 截正方体得到的截面。A=(0,0,0) B=(3,0,0) D=(0,3,0) E=(0,0,3)。截面用红色高亮显示。",
    domain: "general"
  },
  {
    id: "helix",
    icon: "🧬",
    title: "螺旋运动",
    subtitle: "3D · 磁场中的电子轨迹",
    prompt:
      "电子在匀强磁场 B=(0,0,1) 中做螺旋运动，v0=1e5 m/s 与 B 成 30°。画三维螺旋轨迹 Curve，红色球体表示电子，绿色和紫色矢量显示速度和磁场方向。t 滑块驱动动画。",
    domain: "general"
  }
];
